import { Fragment, Suspense, lazy } from 'react';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react-markdown/lib/react-markdown';
import styled, { css } from 'styled-components';

// eslint-disable-next-line no-restricted-imports
import type { RewardBox } from '@sorare/trois/src/components/ClaimRewardBoxes/type';
import ErrorBoundary from 'atoms/ErrorBoundary';
import Button from 'atoms/buttons/Button';
import { FullScreen } from 'atoms/layout/FullScreen';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import LoadingIndicator from 'atoms/loader/LoadingIndicator';
import { HeadlineL, LabelL, LabelS } from 'atoms/typography';
import Dialog from 'components/dialog';
import CloseButton from 'components/dialog/CloseButton';
import { useSentryContext } from 'contexts/sentry';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { glossary } from 'lib/glossary';

import { messages } from './utils';

const ClaimRewardBoxes = lazy(
  async () => import('@sorare/trois/src/components/ClaimRewardBoxes')
);

const centered = css`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
  margin-top: var(--current-stack-height);
`;
const CtaWrapper = styled.div`
  bottom: var(--triple-unit);
  ${centered}
`;
const LabelWrapper = styled(Horizontal)`
  border-radius: var(--double-unit);
  padding: var(--unit) var(--double-unit);
  background-color: rgba(var(--c-rgb-neutral-200), 0.7);
  bottom: calc(10 * var(--unit));
  ${centered}
  pointer-events: none;
`;

type RewardBoxDialogProps = {
  boxes: RewardBox[];
  preloaders: ReactElement[];
  fallbacks: ReactElement[];
  open?: boolean;
  onClose: () => void;
  disableOnClose?: boolean;
};
export const RewardBoxDialog = ({
  boxes,
  preloaders,
  fallbacks,
  open,
  onClose,
  disableOnClose,
}: RewardBoxDialogProps) => {
  const { sendSafeError } = useSentryContext();
  const isTabletAndAbove = useIsTabletAndAbove();

  const {
    flags: { useBoxOpeningRecording = false },
  } = useFeatureFlags();

  if (boxes.length === 0) {
    return null;
  }

  return (
    <>
      {open && (
        <Dialog fullScreen open={open} hideHeader>
          <FullScreen>
            <Suspense fallback={<LoadingIndicator fullScreen />}>
              <ErrorBoundary
                onCatch={error => {
                  sendSafeError(error);
                  // claim all boxes
                  boxes.forEach(b => b.onClaim());
                }}
              >
                {({ error }) =>
                  // On error we claim all boxes and display a 2d version of the content
                  error ? (
                    <Vertical center gap={4}>
                      <HeadlineL as="h1">
                        <FormattedMessage {...messages.hereWhatYouWon} />
                      </HeadlineL>
                      <Horizontal gap={2} center>
                        {fallbacks.map((fallback, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <li key={index}>{fallback}</li>
                        ))}
                      </Horizontal>

                      <LabelS as="span">
                        <FormattedMessage {...messages.fallbackExplanation} />
                      </LabelS>
                    </Vertical>
                  ) : (
                    <ClaimRewardBoxes
                      boxes={boxes}
                      activateRecordings={useBoxOpeningRecording}
                    />
                  )
                }
              </ErrorBoundary>
            </Suspense>
            {!boxes.some(({ opened }) => opened) && isTabletAndAbove && (
              <LabelWrapper>
                <LabelL as="span" color="var(--c-neutral-1000)" bold>
                  <FormattedMessage {...messages.clickToOpenIt} />
                </LabelL>
              </LabelWrapper>
            )}
            {boxes.every(({ opened }) => opened) && (
              <CtaWrapper>
                <Button color="secondary" onClick={onClose} size="medium">
                  <FormattedMessage {...glossary.continue} />
                </Button>
              </CtaWrapper>
            )}
            {!disableOnClose && (
              <CloseButtonWrapper>
                <CloseButton onClose={onClose} />
              </CloseButtonWrapper>
            )}
          </FullScreen>
        </Dialog>
      )}
      {preloaders.map((preloader, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>{preloader}</Fragment>
      ))}
    </>
  );
};
